import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { customerNumbers } = useAuth();
  const [data, setData] = useState(null);
  const [pendingBillingData, setPendingBillingData] = useState([]);
  const [netRevenue, setNetRevenue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trafficData, setTrafficData] = useState(null);
  const [referencesWithPA, setReferencesWithPA] = useState(null);
  const [referencesWithPR, setReferencesWithPR] = useState(null);
  const [enrichedPendingBillingData, setEnrichedPendingBillingData] =
    useState(null);
  const [anticiposByReference, setAnticiposByReference] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [paymentAdvances, setPaymentAdvances] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [shouldFetchData, setShouldFetchData] = useState({
    fetchData: false,
    fetchPaymentAdvances: false,
    fetchReferencePaymentRequests: false,
    fetchAllPaymentAdvances: false,
    fetchPendingBillingData: false,
    fetchGetNetRevenue: false,
    fetchPaymentRequests: false,
    fetchShipments: false,
    fetchAnticiposByReference: false,
    fetchTrafficData: false,
    fetchTrafficDataByReferences: false,
  });
  const [referencesReportData, setReferencesReportData] = useState([]);

  const disableInitialFetch = () => {
    setShouldFetchData(false);
  };

  const fetchData = useCallback(async () => {
    if (!shouldFetchData.fetchData) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        "https://wisdomconnect-ps-4b0861a5d362.herokuapp.com/api/Reports/GetReporteAnticipos"
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
    setIsLoading(false);
  }, [shouldFetchData.fetchData]);

  const triggerRefetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  const fetchPaymentAdvances = useCallback(async () => {
    if (!shouldFetchData.fetchPaymentAdvances) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/references/getReferencesWithPaymentAdvance`
      );
      setReferencesWithPA(response.data);
    } catch (error) {
      console.error("Error fetching payment advances: ", error);
    }
  }, [refetchTrigger, shouldFetchData.fetchPaymentAdvances]);

  useEffect(() => {
    fetchPaymentAdvances();
  }, [fetchPaymentAdvances]);

  const fetchReferencePaymentRequests = useCallback(async () => {
    if (!shouldFetchData.fetchReferencePaymentRequests) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/references/getReferencesWithPaymentRequest`
      );
      setReferencesWithPR(response.data);
    } catch (error) {
      console.error("Error fetching payment references: ", error);
    }
  }, [refetchTrigger, shouldFetchData.fetchReferencePaymentRequests]);

  useEffect(() => {
    fetchReferencePaymentRequests();
  }, [fetchReferencePaymentRequests]);

  const fetchAllPaymentAdvances = useCallback(async () => {
    if (!shouldFetchData.fetchAllPaymentAdvances) return;
    setIsLoading(true);
    try {
      // Adjust the URL according to your actual API endpoint
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance`
      );
      setPaymentAdvances(response.data); // Store the fetched payment advances in state
    } catch (error) {
      console.error("Error fetching all payment advances: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetchData.fetchAllPaymentAdvances]);

  useEffect(() => {
    fetchAllPaymentAdvances(); // Fetch all payment advances on component mount
  }, [fetchAllPaymentAdvances]);

  const fetchPendingBillingData = useCallback(async () => {
    if (!shouldFetchData.fetchPendingBillingData) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/pending_billing`
      );
      setPendingBillingData(response.data || []); // Fallback to an empty array if null/undefined
    } catch (error) {
      console.error("Error fetching pending billing data: ", error);
      setPendingBillingData([]);
    }
    setIsLoading(false);
  }, [shouldFetchData.fetchPendingBillingData]); // Dependencies array is empty, so this callback is created once per component lifecycle

  useEffect(() => {
    fetchPendingBillingData(); // Fetch pending billing data when the component mounts
  }, [fetchPendingBillingData]); // Dependency on the memoized fetch function

  const fetchGetNetRevenue = useCallback(async () => {
    if (!shouldFetchData.fetchGetNetRevenue) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/net_revenue/`
      );
      setNetRevenue(response.data);
    } catch (error) {
      console.error("Error fetching net revenue data: ", error);
    }
    setIsLoading(false);
  }, [shouldFetchData.fetchGetNetRevenue]); //  Memoized and not recreated on every render

  const fetchPaymentRequests = useCallback(async () => {
    if (!shouldFetchData.fetchPaymentRequests) return;
    setIsLoading(true);
    try {
      // Update the URL to your actual API endpoint for fetching payment requests
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request`
      );
      setPaymentRequests(response.data);
    } catch (error) {
      console.error("Error fetching payment requests: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetchData.fetchPaymentRequests]);

  useEffect(() => {
    fetchPaymentRequests(); // Fetch payment requests on component mount
  }, [fetchPaymentRequests]);

  const fetchShipments = useCallback(async () => {
    if (!shouldFetchData.fetchShipments) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/ff_shipment`
      );
      setShipments(response.data);
    } catch (error) {
      console.error("Error fetching shipments: ", error);
    }
    setIsLoading(false);
  }, [shouldFetchData.fetchShipments]);

  const fetchDataBasedOnFilter = useCallback(
    async (filters) => {
      if (!shouldFetchData.fetchDataBasedOnFilter) return;
      setIsLoading(true);
      try {
        let filteredData;
        if (filters.carrierNames && filters.carrierNames.length > 0) {
          filteredData = shipments.filter((shipment) =>
            filters.carrierNames.includes(shipment.carrierName)
          );
        } else {
          // Fetch original data if no filters are applied
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/ff_shipment`
          );
          filteredData = response.data;
        }
        setShipments(filteredData);
      } catch (error) {
        console.error("Error filtering data: ", error);
      } finally {
        setIsLoading(false);
      }
    },
    [shipments, shouldFetchData.fetchDataBasedOnFilter]
  );

  useEffect(() => {
    fetchGetNetRevenue();
  }, [fetchGetNetRevenue]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const fetchAnticiposByReference = useCallback(
    async (trafficReference) => {
      if (!shouldFetchData.fetchAnticiposByReference) return;
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/callReferenciaCuentaAnticipo`,
          { traffic_reference: trafficReference }
        );
        setAnticiposByReference(response.data);
      } catch (error) {
        console.error("Error fetching anticipos by reference: ", error);
      }
      setIsLoading(false);
    },
    [shouldFetchData.fetchAnticiposByReference]
  );

  useEffect(() => {
    fetchAnticiposByReference();
  }, [fetchAnticiposByReference]);

  useEffect(() => {
    fetchGetNetRevenue();
  }, [fetchGetNetRevenue]);

  const enrichPendingBillingData = () => {
    if (!pendingBillingData || !data) return;

    const enrichedData = pendingBillingData.map((billingEntry) => {
      const fetchDataEntry = data.find(
        (d) => d.traffic_reference === billingEntry.Trafico
      );
      return fetchDataEntry
        ? { ...billingEntry, container_data: fetchDataEntry.container_data }
        : billingEntry;
    });

    setEnrichedPendingBillingData(enrichedData);
  };

  const fetchTrafficData = useCallback(async () => {
    if (!shouldFetchData.fetchTrafficData) return;
    try {
      setIsLoading(true);

      // Convert to strings to support both numeric and alphanumeric customer IDs
      const formattedCustomerNumbers = customerNumbers
        .filter((num) => num)
        .map((num) => num.toString().trim())
        .filter((num) => num.length > 0);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
        {
          params: {
            customer_id:
              formattedCustomerNumbers.length > 0
                ? formattedCustomerNumbers
                : [],
            refresh: true,
          },
        }
      );
      setTrafficData(response.data);
    } catch (error) {
      console.error("Error fetching traffic data: ", error);
    }
    setIsLoading(false);
  }, [shouldFetchData.fetchTrafficData, customerNumbers]);

  const fetchTrafficDataByReferences = useCallback(
    async (references) => {
      if (!shouldFetchData.fetchTrafficDataByReferences && !references) return;

      setIsLoading(true);
      try {
        // Convert to array if single reference
        const referencesArray = Array.isArray(references)
          ? references
          : [references];

        // Create the API params with multiple reference parameters
        const params = {};

        // Add each reference as a separate query parameter with the same key
        referencesArray.forEach((ref) => {
          // If params.reference doesn't exist, create it as an array
          if (!params.reference) {
            params.reference = [];
          }
          // Push the reference to the array
          params.reference.push(ref);
        });

        console.log("Fetching traffic data for references:", referencesArray);

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
          { params }
        );

        setReferencesReportData(response.data);
        return response.data; // Also return data for immediate use
      } catch (error) {
        console.error("Error fetching traffic data by references: ", error);
        console.error("Error details:", error.response?.data || error.message);
        return []; // Return empty array in case of error
      } finally {
        setIsLoading(false);
      }
    },
    [shouldFetchData.fetchTrafficDataByReferences]
  );

  useEffect(() => {
    if (shouldFetchData.fetchData) {
      fetchData();
    }
  }, [fetchData, shouldFetchData.fetchData]);

  useEffect(() => {
    enrichPendingBillingData();
  }, [data, pendingBillingData]);

  return (
    <DataContext.Provider
      value={{
        data,
        trafficData,
        pendingBillingData: enrichedPendingBillingData,
        isLoading,
        referencesWithPA,
        referencesWithPR,
        netRevenue,
        anticiposByReference,
        shipments,
        paymentAdvances,
        paymentRequests,
        fetchPendingBillingData,
        fetchTrafficData,
        customerNumbers,
        fetchTrafficDataByReferences,
        fetchData,
        fetchGetNetRevenue,
        fetchAnticiposByReference,
        fetchShipments,
        fetchDataBasedOnFilter,
        fetchAllPaymentAdvances,
        fetchPaymentRequests,
        fetchReferencePaymentRequests,
        triggerRefetch,
        disableInitialFetch,
        setShouldFetchData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
